import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditTopicComponent } from 'src/app/components/edit-topic/edit-topic.component';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { WhereClause } from 'src/models';
import { ProjectMember } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AgentTopicsService } from 'src/services/agent-topics.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    SharedDirectivesModule,
    NgxDatatableModule,
    SharedPipesModule,
    RouterModule,
  ],
  selector: 'app-knowledge-base',
  templateUrl: './agent-topics.component.html',
  styleUrls: ['./agent-topics.component.scss'],
})
export class AgentTopicsComponent implements OnInit {
  projectId: string;

  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  routerSub: Subscription;

  isLoading = true;
  sortField = 'name';
  sortDesc = false;
  limit = 25;
  searchText: string;

  fetchTopics = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<ProjectMember>>> => {
    const options: any = {
      projectId: this.projectId,
      limit,
      order,
      descending,
      cursor,
    };

    const where: WhereClause[] = [];

    if (this.projectId) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.projectId,
      });
    }

    if (this.searchText) {
      where.push({
        field: 'name',
        operator: 'like',
        value: `%${this.searchText}%`,
      });
    }

    if (where.length) {
      options.where = where;
    }

    const result = await this.agentTopicsService.list(options);

    return result;
  };

  topicsDataSource = createDataSource(this.fetchTopics);

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private agentTopicsService: AgentTopicsService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private msgService: MessageService,
    private navCtrl: NavController,
    private router: Router,
    private location: Location,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.loadTopics();
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        const modal = await this.modalCtrl.create({
          component: EditTopicComponent,
          cssClass: 'modal-tall',
          componentProps: {
            id: params.id,
            projectId: this.projectId,
          },
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.navCtrl.navigateForward(this.router.url, {
            queryParams: {},
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async loadTopics() {
    this.isLoading = true;
    try {
      await this.topicsDataSource.firstPage();
    } catch (e) {
      this.msgService.showError(e);
    } finally {
      this.isLoading = false;
    }
  }

  handleSearch(event: any) {
    this.searchText = event.target.value;
    this.loadTopics();
  }

  async openCreateTopicModal() {
    const modal = await this.modalCtrl.create({
      component: EditTopicComponent,
      cssClass: 'modal-tall',
      componentProps: {
        projectId: this.projectId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.loadTopics();
    });
  }

  editTopic(topic) {
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        id: topic.id,
      },
    });
  }

  async deleteTopic(topic) {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              await this.agentTopicsService.delete(topic.id);
              this.loadTopics();
            } catch (e) {
              this.msgService.show(`Unable to delete topic: ${e}`);
            }
          },
        },
      ],
    });
    alert.present();
  }
}
